import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Check } from 'lucide-react';
import { unifiedControlStyle, dropdownPalette, DropdownGlobalStyles } from './ReusableDropdown';

const ReusableMultiSelect = ({
  items = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select...',
  emptyMessage = 'No results found',
  maxItems = 8,
  getItemKey = (item) => item.id || item.email,
  getItemLabel = (item) => item.name || item.email,
  filterFunction,
  renderItem,
  theme = 'light',
  language = 'en',
  disabled = false,
  inputStyle = {},
  dropdownStyle = {},
  itemStyle = {},
  showAllOnArrowClick = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showingAll, setShowingAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelectedIds, setInternalSelectedIds] = useState(selectedIds || []);

  // Sync internal state when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      setInternalSelectedIds(selectedIds || []);
    }
  }, [showDropdown, selectedIds]);

  const handleCloseDropdown = () => {
    setShowDropdown(false);
    setSearchTerm('');
    setShowingAll(false);
    // Fire onChange with the final selections when closing
    if (JSON.stringify(internalSelectedIds) !== JSON.stringify(selectedIds)) {
      onChange(internalSelectedIds);
    }
  };
  
  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const inputRef = useRef(null);
  
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const isRTL = language === 'ar';

  const defaultFilterFunction = (item, search) => {
    const label = String(getItemLabel(item) || '').toLowerCase();
    const s = String(search || '').toLowerCase();
    return label.includes(s);
  };

  useEffect(() => {
    if (showingAll) {
      setFilteredItems(items || []);
      setHighlightedIndex(-1);
      return;
    }
    if (!String(searchTerm || '').trim()) {
      setFilteredItems((items || []).slice(0, maxItems));
      setHighlightedIndex(-1);
      return;
    }
    const filterFunc = filterFunction || defaultFilterFunction;
    const filtered = (items || [])
      .filter(item => filterFunc(item, searchTerm))
      .slice(0, maxItems);
    setFilteredItems(filtered);
    setHighlightedIndex(-1);
  }, [searchTerm, items, maxItems, showingAll, filterFunction, getItemLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = containerRef.current;
      const portal = portalRef.current;
      if (
        container && !container.contains(event.target) &&
        (!portal || !portal.contains(event.target))
      ) {
        handleCloseDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // Re-bind on every selection change — an empty dep array here would
    // permanently capture the mount-time (empty) internalSelectedIds in the
    // closure, so an outside click would always commit an empty selection
    // no matter what the user actually checked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalSelectedIds, selectedIds, onChange]);

  const updateDropdownCoords = () => {
    const inputEl = containerRef.current;
    if (!inputEl) return;
    const rect = inputEl.getBoundingClientRect();
    const headerHeight = 48;
    const itemHeight = 44;
    const maxHeight = 300;
    const estimatedHeight = Math.min(maxHeight, headerHeight + (filteredItems.length * itemHeight));
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setDropdownDirection(openUp ? 'up' : 'down');
    setDropdownCoords({
      top: openUp ? (rect.top - estimatedHeight) : rect.bottom,
      left: rect.left,
      width: rect.width,
      height: estimatedHeight,
    });
  };

  useEffect(() => {
    if (!showDropdown) return;
    updateDropdownCoords();
    const handler = () => updateDropdownCoords();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [showDropdown, showingAll, filteredItems.length]);

  const toggleSelection = (itemId, e) => {
    if (e) e.stopPropagation();
    setInternalSelectedIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && !searchTerm && internalSelectedIds.length > 0) {
      e.preventDefault();
      setInternalSelectedIds(prev => prev.slice(0, -1));
      return;
    }
    if (!showDropdown || filteredItems.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredItems.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredItems.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          toggleSelection(getItemKey(filteredItems[highlightedIndex]));
        }
        break;
      case 'Escape':
        handleCloseDropdown();
        break;
      default:
        break;
    }
  };

  const p = dropdownPalette(theme);
  const hasSelection = internalSelectedIds.length > 0;

  const baseInputContainerStyle = {
    ...unifiedControlStyle(theme, isRTL),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    padding: hasSelection ? '6px 10px' : '10px 14px',
    paddingInlineEnd: '42px',
    cursor: disabled ? 'not-allowed' : 'text',
    minHeight: '44px',
    background: disabled ? p.disabledBg : p.inputBg,
    opacity: disabled ? 0.65 : 1,
    direction: isRTL ? 'rtl' : 'ltr',
    ...inputStyle,
    ...(showDropdown && { borderColor: p.primary, boxShadow: `0 0 0 3px ${p.ring}` }),
  };
  // Keep room for the arrow button even when inputStyle sets `padding`.
  delete baseInputContainerStyle.paddingLeft;
  delete baseInputContainerStyle.paddingRight;
  baseInputContainerStyle.paddingInlineEnd = '42px';

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '4px 6px 4px 10px',
    paddingInlineStart: '10px',
    paddingInlineEnd: '6px',
    borderRadius: '999px',
    color: p.primary,
    background: p.primarySoft,
    maxWidth: '100%',
  };

  const baseDropdownStyle = {
    background: p.menuBg,
    border: `1px solid ${p.menuBorder}`,
    boxShadow: p.menuShadow,
    borderRadius: '12px',
    zIndex: 99999,
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '6px',
    boxSizing: 'border-box',
    direction: isRTL ? 'rtl' : 'ltr',
    animation: 'rdd-in 0.12s ease-out',
    ...dropdownStyle,
  };

  const baseItemStyle = {
    padding: '9px 10px',
    margin: '1px 0',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: p.text,
    transition: 'background 0.1s ease',
    textAlign: 'start',
    ...itemStyle,
  };

  const checkBox = (checked) => (
    <span
      aria-hidden="true"
      style={{
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        border: `1.5px solid ${checked ? p.primary : p.border}`,
        background: checked ? p.primary : 'transparent',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.12s ease',
      }}
    >
      {checked && <Check size={12} strokeWidth={3} />}
    </span>
  );

  const allVisibleSelected =
    filteredItems.length > 0 &&
    filteredItems.every((item) => internalSelectedIds.includes(getItemKey(item)));

  const toggleAllVisible = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const visibleIds = filteredItems.map((item) => getItemKey(item));
    setInternalSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : [...prev, ...visibleIds.filter((id) => !prev.includes(id))],
    );
  };

  return (
    <div ref={containerRef} data-reusable-dropdown="true" style={{ position: 'relative', width: '100%' }}>
      <div
        style={baseInputContainerStyle}
        onClick={() => {
          if (disabled) return;
          setShowDropdown(true);
          setShowingAll(true);
          if (inputRef.current) inputRef.current.focus();
        }}
      >
        {internalSelectedIds.map(id => {
          const item = items.find(i => getItemKey(i) === id);
          if (!item) return null;
          return (
            <span key={id} style={chipStyle} onClick={(e) => e.stopPropagation()}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getItemLabel(item)}
              </span>
              <button
                type="button"
                aria-label={isRTL ? 'إزالة' : 'Remove'}
                onClick={(e) => toggleSelection(id, e)}
                disabled={disabled}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: '1px',
                  borderRadius: '50%',
                  display: 'flex',
                  opacity: 0.75,
                }}
              >
                <X size={13} />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowingAll(false);
            setShowDropdown(true);
          }}
          onFocus={() => {
            if (!showDropdown) {
              setShowDropdown(true);
              setShowingAll(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={hasSelection ? '' : placeholder}
          disabled={disabled}
          autoComplete="off"
          style={{
            border: 'none',
            background: 'transparent',
            outline: 'none',
            color: p.text,
            fontSize: '14px',
            fontFamily: 'inherit',
            minWidth: '60px',
            flex: 1,
            padding: hasSelection ? '4px 2px' : 0,
            textAlign: 'start',
          }}
        />

        <div style={{
          position: 'absolute',
          insetInlineEnd: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
        }}>
          {showAllOnArrowClick && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={isRTL ? 'فتح القائمة' : 'Toggle options'}
              onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                if (showDropdown) {
                  handleCloseDropdown();
                } else {
                  setShowingAll(true);
                  setShowDropdown(true);
                  if (inputRef.current) inputRef.current.focus();
                }
              }}
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: showDropdown ? p.primary : p.muted,
                width: '26px',
                height: '26px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.2s ease, color 0.15s ease',
                transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronDown size={18} />
            </button>
          )}
        </div>
      </div>

      {showDropdown && (typeof document !== 'undefined' && dropdownCoords) && createPortal(
        <div
          className="reusable-dropdown-portal"
          ref={portalRef}
          role="listbox"
          aria-multiselectable="true"
          style={{
            ...baseDropdownStyle,
            position: 'fixed',
            top: (dropdownDirection === 'down' ? dropdownCoords.top + 6 : dropdownCoords.top - 6) + 'px',
            left: dropdownCoords.left + 'px',
            width: dropdownCoords.width + 'px',
          }}
        >
          {filteredItems.length === 0 ? (
            <div style={{ padding: '18px 12px', textAlign: 'center', color: p.muted, fontSize: '14px' }}>
              {String(searchTerm || '').trim()
                ? (emptyMessage || (isRTL ? 'لا توجد نتائج' : 'No results found'))
                : (isRTL ? 'لا توجد خيارات' : 'No options')}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                padding: '6px 10px 8px',
                marginBottom: '4px',
                borderBottom: `1px solid ${p.divider}`,
                fontSize: '12px',
                color: p.muted,
                fontWeight: 600,
              }}
            >
              <span>
                {isRTL
                  ? `${internalSelectedIds.length} محدد من ${items.length}`
                  : `${internalSelectedIds.length} of ${items.length} selected`}
              </span>
              <button
                type="button"
                onMouseDown={toggleAllVisible}
                style={{
                  border: 'none',
                  background: 'none',
                  color: p.primary,
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '2px 4px',
                  fontFamily: 'inherit',
                }}
              >
                {allVisibleSelected
                  ? (isRTL ? 'إلغاء التحديد' : 'Clear all')
                  : (isRTL ? 'تحديد الكل' : 'Select all')}
              </button>
            </div>
          )}

          {filteredItems.map((item, index) => {
            const isHighlighted = index === highlightedIndex;
            const itemKey = getItemKey(item);
            const isSelected = internalSelectedIds.includes(itemKey);

            return (
              <div
                key={itemKey}
                role="option"
                aria-selected={isSelected}
                style={{
                  ...baseItemStyle,
                  background: isHighlighted ? p.hover : (isSelected ? p.primarySoft : 'transparent'),
                  fontWeight: isSelected ? 600 : 500,
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => toggleSelection(itemKey, e)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {checkBox(isSelected)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renderItem ? renderItem(item, theme, language) : getItemLabel(item)}
                </div>
              </div>
            );
          })}
        </div>,
        document.body
      )}

      <DropdownGlobalStyles theme={theme} />
    </div>
  );
};

export default ReusableMultiSelect;
